import { DiscoveryLevel, FeatureType, IntegrationException, PreviewType, RoleAssignment } from '../types';
import { LitElement, css, html } from 'lit';
import { createTopicRequestFromTopic, getViewsForTopic } from '../helpers/api/topics-api';
import { customElement, property, query, state } from 'lit/decorators';
import { delay, isDefined, undefinedIfEmpty } from '../helpers/utils';

import { Dialog } from '@material/mwc-dialog';
import { MapSharingComponent } from './admin/map-sharing-component';
import { Menu } from '@material/mwc-menu';
import { StateController } from '../helpers/state/state-management/state-controller';
import { UpgradeDialogElement } from './upgrade-dialog';
import accountInfo from '../helpers/state/account-info';
import featuresInfo from '../helpers/state/features-info';
import { ifDefined } from 'lit/directives/if-defined';
import { logWithAmplitude } from '../helpers/api/api';
import organizationInfo from '../helpers/state/organization-info';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';

@customElement('map-header-buttons')
export class MapHeaderButtons extends LitElement {
    static amplitude_callsite: string = 'Share Buttons Group';

    @query('#duplicationModal')
    duplicationModal?: Dialog;

    @query('#shareModal')
    shareModal?: Dialog;

    @query('#shareElement')
    shareElement?: MapSharingComponent;

    @query('#privacyMenu')
    privacyMenu!: Menu;

    @query('#menuConnect')
    menuConnect!: HTMLDivElement;

    @query('#upgradeModal')
    upgradeModal!: Dialog;

    @query('#customAdvancedInsightsModal')
    customAdvancedInsightsModal?: Dialog;

    @query('#upgradeDialogInnerComponent')
    upgradeDialogInnerComponent!: UpgradeDialogElement;

    @property({ type: Array })
    rolesForOrganization?: RoleAssignment[];

    @property({ type: Array })
    rolesForTopic?: RoleAssignment[];

    @property({ type: String })
    viewonlyLink?: string;

    @property({ type: String })
    crowdsourceLink?: string;

    @property( { type: Boolean })
    isCollabSettings: boolean = false;

    @property()
    readEmbed?: string;

    @property()
    addPointEmbed?: string;

    @state()
    showEmbedOnly: boolean = false;

    @state()
    topicViewCount: number = 0;

    @state()
    featureType: FeatureType =  FeatureType.PrintableMap;

    @state()
    userPreview: boolean = false;

    @state()
    previewMode: PreviewType = PreviewType.Desktop;

    @state()
    isCreating: boolean = false;

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);
    private readonly bindPointCacheState = new StateController(this, pointCache);

    async firstUpdated() {
        const telemetryEntries = await getViewsForTopic(
            MapHeaderButtons.amplitude_callsite,
            topicData.topicId,
            topicData.topicKey
        );

        this.privacyMenu.anchor = this.menuConnect;

        this.topicViewCount = telemetryEntries
            .map((entry) => entry.value)
            .reduce((agg, v) => agg + v, 0);
    }

    private async shareMapClick() {

        logWithAmplitude(MapHeaderButtons.amplitude_callsite, 'Share Map Click');
        this.showEmbedOnly = false;
        if (isDefined(this.shareModal) && !this.shareModal.open) {
            this.shareModal.show();
        }

        await this.createViewOnlyLInk();
        await this.createCollaboratorLink();
        this.requestUpdate();
    }


    private async getFormLink() {
        logWithAmplitude(MapHeaderButtons.amplitude_callsite, 'Share Map Click');
        this.showEmbedOnly = false;
        if (isDefined(this.shareModal) && !this.shareModal.open) {
            this.shareModal.show();
        }

        await this.createViewOnlyLInk();
        await this.createCollaboratorLink();

        // new event
        await delay(2000);
        const event = new CustomEvent('change-tab', {
            detail: {
                tabIndex: 2,
                tabName: 'form'
            }
        });
        this.shareElement?.handleChangeTab(event);
        this.requestUpdate();
    }

    private async createViewOnlyLInk() {
        // Always use the keyed read link so shared/embedded links keep working
        // even if the map is later flipped from searchable to unlisted.
        this.viewonlyLink = undefinedIfEmpty(topicData.swapUrlForOrgUrl(topicData.topic?.cc_read_link ?? ''));
    }

    private async createCollaboratorLink() {
        if (topicData.topic?.is_addpointer === true) {
            // Always use the keyed addpoint link for the same reason.
            this.crowdsourceLink = undefinedIfEmpty(topicData.swapUrlForOrgUrl(topicData.topic?.cc_addpoint_link ?? ''));
        }
    }

    private duplicateMapClick() {
        logWithAmplitude(MapHeaderButtons.amplitude_callsite, 'Duplicate Map Click');

        if (featuresInfo.duplicateMapEnabled) {
            if (isDefined(this.duplicationModal) && !this.duplicationModal.open) {
                this.duplicationModal.show();
            }
        } else {
            logWithAmplitude(MapHeaderButtons.amplitude_callsite, 'Upgrade Dialog Opened');
            this.openUpgradeDialog({ detail: { featureType: FeatureType.DuplicateMap } });
        }
    }

    private async changeMapPrivacy(level: DiscoveryLevel) {
        if (!featuresInfo.makeMapsPrivateEnabled && level === DiscoveryLevel.Private) {
            logWithAmplitude(MapHeaderButtons.amplitude_callsite, 'Upgrade Dialog Opened');
            this.featureType = FeatureType.MakeMapsPrivate;
            this.upgradeModal.show();
        } else {
            const CreateTopicRequest = createTopicRequestFromTopic(topicData.topic);
            CreateTopicRequest.discovery_level = level;
            await topicData.fetchAndUpdateTopicData(CreateTopicRequest,MapHeaderButtons.amplitude_callsite);
        }
    }

    private openUpgradeDialog(e: CustomEvent | any) {

        // close the share modal if it is open
        if (isDefined(this.shareModal) && this.shareModal.open) {
            this.shareModal.close();
        }
        if (isDefined(this.upgradeModal) && !this.upgradeModal.open) {
            this.upgradeModal.show();
            this.featureType = e.detail.featureType ?? FeatureType.PrintableMap;
        }

        if (isDefined(this.shareModal) && this.shareModal.open) {
            this.shareModal.close();
        }
    }

    private async embedMapClick() {
        logWithAmplitude(MapHeaderButtons.amplitude_callsite, 'Embed Map Click');

        this.showEmbedOnly = true;
        await this.createViewOnlyLInk();
        await this.createCollaboratorLink();

        if (isDefined(this.shareModal) && !this.shareModal.open) {
            this.shareModal.show();
        }
    }

    private async updateUserPreviewStatus() {
        this.userPreview = !this.userPreview;
        if (!this.userPreview) {
            this.previewMode = PreviewType.Desktop;
        }
        this.dispatchEvent(new CustomEvent('set_preview_status'));
    }

    private changePreviewType(e: CustomEvent | any) {
        if (e.detail.tabIndex === 0) {
            this.previewMode = PreviewType.Desktop;
        } else {
            this.previewMode = PreviewType.Mobile;
        }
        this.dispatchEvent(new CustomEvent('set_preview_mode', { detail: { previewMode: this.previewMode } }));
    }

    private printMapClick() {

        const shareLink = this.viewonlyLink;
        const printableLink = undefinedIfEmpty(topicData.topicKey) === undefined ? `/#print?topic_id=${topicData.topicId}` : `/#print?topic_id=${topicData.topicId}&topic_key=${topicData.topicKey}`;

        const w = window.open(shareLink);
        w?.location.replace(printableLink ?? '');
        logWithAmplitude(MapHeaderButtons.amplitude_callsite, 'Print Map Click');
    }

    private async openCustomInsightsModal(e: CustomEvent | any) {
        // dispatch event to parent page to open the modal
        this.dispatchEvent(new CustomEvent('open-custom-insights-modal'));
    }

    render() {
        return html`
            ${this.isCollabSettings ? this.renderCollabButtons() : this.renderAdminPortal()}
            <mwc-dialog
                id="duplicationModal"
                class="dialog"
                hideActions
            >
                <modal-dialog size="md" @close="${() => this.duplicationModal?.close()}">
                    <duplicate-map-component
                        .topic=${topicData.topic}
                        slot="body"
                        @on-topic-duplicated="${() => this.duplicationModal?.close()}"
                        @close-upgrade="${() => this.duplicationModal?.close()}">
                    </duplicate-map-component>
                </modal-dialog>
            </mwc-dialog>

            ${this.viewonlyLink ?? this.crowdsourceLink ? html`
                <mwc-dialog
                    id="shareModal"
                    class="dialog"
                    hideActions
                >
                    <modal-dialog size="md" @close="${() => this.shareModal?.close()}">
                        <map-sharing-component
                            slot="body"
                            id="shareElement"
                            .mode="${this.isCollabSettings ? 'collaborative' : 'view-only'}"
                            .showEmbedOnly="${this.showEmbedOnly}"
                            .crowdsourceLink="${this.crowdsourceLink ?? 'loading...'}"
                            socialMediaShareImage="${ifDefined(topicData.topic?.custom_social_media_image)}"
                            .viewonlyLink="${this.viewonlyLink ?? 'loading...'}"
                            @open-upgrade-dialog=${(e: CustomEvent) => this.openUpgradeDialog(e)}>
                        </map-sharing-component>
                    </modal-dialog>
                </mwc-dialog>
                ` : ''}

            <mwc-dialog
                id="upgradeModal"
                class="dialog"
                hideActions
            >
                <modal-dialog size="md" @close="${() => this.upgradeModal.close()}">
                    <upgrade-dialog
                        id="upgradeDialogInnerComponent"
                        slot="body"
                        .featureType="${this.featureType}"
                        @close="${() => this.upgradeModal.close()}"
                    >
                    </upgrade-dialog>
                </modal-dialog>
            </mwc-dialog>
        `;
    }

    renderCollabButtons() {
        this.style.background = 'transparent';
        this.style.padding = '0';
        this.style.width = 'auto';
        return html`
            <form-button
                size="xs"
                variant="outline"
                .textColor=${topicData.textColor}
                @click="${this.shareMapClick}">
                <mwc-icon class="shareButtonIcon">share</mwc-icon>
                Get Collaborative Link
            </form-button>
            <form-button
                size="xs"
                variant="outline"
                .textColor=${topicData.textColor}
                @click="${this.getFormLink}">
                <mwc-icon class="shareButtonIcon">share</mwc-icon>
                Get Form Link
            </form-button>
        `;
    }

    renderAdminPortal() {
        return html`
            <div class="map-header-buttons">
                <div class="metadata-container">
                    ${this.userPreview ? html`
                        <span class="topic-metadata">You are currently previewing the map.</span>
                    ` : html`
                        <form-button variant="outline"
                            size="sm"
                            id='menuConnect'
                            class="buttonSection ${this.userPreview || topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations || organizationInfo.isGuest ? 'hidden' : ''}"
                            title="${topicData.discoveryLevel === DiscoveryLevel.Private
                                ? 'Unlisted: your map is not indexed by search engines or shown on Proxi Discover. Anyone with a link you share can still view it.'
                                : 'Searchable: your map can be found on Google and Proxi Discover. Click to change visibility.'}"
                            @click=${() => (this.privacyMenu.open = true)}>
                            ${featuresInfo.makeMapsPrivateEnabled
                                ? ''
                                : html`<img class="leftPng" src="/images/tiers/starter_small.png" />`
                            }
                            ${topicData.discoveryLevel === DiscoveryLevel.Private ? html`
                                <mwc-icon class="shareButtonIcon">public_off</mwc-icon>
                                <span class="">Unlisted</span>
                            ` : html`
                                <mwc-icon class="shareButtonIcon">public</mwc-icon>
                                <span class="">Searchable</span>
                            `}
                        </form-button>
                        <mwc-menu id="privacyMenu" style="position: relative;left: -200px;top: -10px;">
                            <div class="privacy-status-row">
                                <div class="privacy-status-title">
                                    Currently
                                    <b>${topicData.discoveryLevel === DiscoveryLevel.Private ? 'Unlisted' : topicData.discoveryLevel === DiscoveryLevel.ReadPublic ? 'Searchable (View Only)' : 'Searchable (Collaborative)'}</b>
                                </div>
                                <div class="privacy-description">
                                    ${topicData.discoveryLevel === DiscoveryLevel.Private
                                        ? 'Hidden from search. Shared links still work.'
                                        : topicData.discoveryLevel === DiscoveryLevel.ReadPublic
                                            ? 'Indexed by Google and Proxi Discover. Viewers can explore but not add points.'
                                            : 'Indexed by Google and Proxi Discover. Viewers can also suggest points.'}
                                </div>
                            </div>
                            <li divider role="separator"></li>
                            ${topicData.discoveryLevel !== DiscoveryLevel.Private ? html`
                                <div class="privacy-option" role="button" tabindex="0"
                                    @click="${async () => await this.changeMapPrivacy(DiscoveryLevel.Private)}"
                                    title="Hide this map from Google and Proxi Discover. Anyone you send a link to can still view it — existing shared links, QR codes, and embeds keep working.">
                                    <div class="privacy-option-title">
                                        <mwc-icon class="shareButtonIcon">public_off</mwc-icon>
                                        <span>Change to Unlisted</span>
                                    </div>
                                    <div class="privacy-description">Hidden from search. Shared links still work.</div>
                                </div>
                            ` : ''}
                            ${topicData.discoveryLevel !== DiscoveryLevel.ReadPublic ? html`
                                <div class="privacy-option" role="button" tabindex="0"
                                    @click="${async () => await this.changeMapPrivacy(DiscoveryLevel.ReadPublic)}"
                                    title="Let Google and Proxi Discover index this map so people can find it. Viewers can explore the map but cannot add or suggest points.">
                                    <div class="privacy-option-title">
                                        <mwc-icon class="shareButtonIcon">public</mwc-icon>
                                        <span>Change to Searchable (View Only)</span>
                                    </div>
                                    <div class="privacy-description">Indexed by Google. Viewers cannot add points.</div>
                                </div>
                            ` : ''}
                            ${topicData.discoveryLevel !== DiscoveryLevel.AddpointPublic ? html`
                                <div class="privacy-option" role="button" tabindex="0"
                                    @click="${async () => await this.changeMapPrivacy(DiscoveryLevel.AddpointPublic)}"
                                    title="Let Google and Proxi Discover index this map, and allow anyone who views it to suggest or add new points.">
                                    <div class="privacy-option-title">
                                        <mwc-icon class="shareButtonIcon">public</mwc-icon>
                                        <span>Change to Searchable (Collaborative)</span>
                                    </div>
                                    <div class="privacy-description">Indexed by Google. Viewers can also suggest points.</div>
                                </div>
                            ` : ''}
                        </mwc-menu>
                        ${topicData.isOwner ?
                            html`
                                <form-button variant="outline"
                                    size="sm"
                                    id="duplicateBtn"
                                    class="buttonSection ${this.userPreview ? 'hidden' : ''}"
                                    @click="${this.duplicateMapClick}"
                                >
                                    ${featuresInfo.duplicateMapEnabled
                                        ? ''
                                        : html`<img class="leftPng" src="/images/tiers/starter_small.png" />`
                                    }
                                    <mwc-icon class="shareButtonIcon">queue</mwc-icon>
                                    <span class="buttonText">Duplicate</span>
                                </form-button>
                            `
                            : ''
                        }
                        ${organizationInfo.isGuest ? html`
                            You are currently viewing this map as a guest to the ${organizationInfo.organization?.name}. For full access, request team access from the workspace owner.
                        ` : ''}
                    `}
                </div>

                <div class="buttons-container">
                        <form-button
                            variant="outline"
                            size="sm"
                            class="buttonSection ${this.userPreview || topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations || organizationInfo.isGuest ? 'hidden' : ''}"
                            @click="${this.printMapClick}">
                            ${featuresInfo.printableMapEnabled ? '' : html`
                                <img class="leftPng" src="/images/tiers/starter_small.png" />
                            `}
                            <mwc-icon class="shareButtonIcon">print</mwc-icon>
                            <span class="buttonText">Print</span>
                        </form-button>

                        <form-button
                            variant="outline"
                            size="sm"
                            class="buttonSection ${this.userPreview ? 'hidden' : ''}"
                            @click="${this.embedMapClick}">
                            <img class="shareButtonImg" src="/images/ic_embed.svg" />
                            <span class="buttonText">Embed</span>
                        </form-button>

                        <tabs-component
                            id="tabsComponent"
                            class="${this.userPreview ? '' : 'hidden'}"
                            .options=${['Desktop', 'Mobile']}
                            @on-select-tab=${(e: CustomEvent) => {
                                this.changePreviewType(e);
                            }}
                        ></tabs-component>

                        <form-button
                            variant="outline"
                            size="sm"
                            id="userPreviewBtn"
                            class="buttonSection"
                            @click="${this.updateUserPreviewStatus}"
                        >
                            ${!this.userPreview ? html`
                                <mwc-icon class="shareButtonIcon">preview</mwc-icon>
                                Preview` : html`
                                <mwc-icon class="shareButtonIcon">close</mwc-icon>
                                Exit Preview Mode`
                            }
                        </form-button>

                        <form-button
                            size="sm"
                            id="shareBtn"
                            class="buttonSection ${this.userPreview ? 'hidden' : ''}"
                            @click="${this.shareMapClick}">
                            <mwc-icon class="shareButtonIcon">share</mwc-icon>
                            Share
                        </form-button>
                </div>
            </div>


        `;
    }

    static get styles() {
        return css`
            :host {
                width: 100%;
                padding: 0.5rem 26px 0.5rem 38px;
                background: #F0EFF2;
                box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.05), 0px 2px 2px -2px rgba(31, 27, 45, 0.08);
                box-sizing: border-box;
                --mdc-menu-min-width: min(100vw, 150px);
                --mdc-typography-subtitle1-font-size: 12px;
                --mdc-menu-item-height: 2rem;
            }

            .divider {
                width: 1px;
                height: 1.5rem;
                margin: 0 1rem;
                background: var(--theme-input-color);
            }

            .flexContainer {
                display: flex;
                align-items: flex-start;
                flex-direction: column;
                flex-wrap: wrap;
                font-size: 0.8rem;
                line-height: 0.85rem;
            }

            .map-header-buttons {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 10px 20px;
            }

            .metadata-container {
                display: flex;
                align-items: center;
                justify-content: flex-start;
                flex-wrap: wrap;
                flex-direction: row;
                flex: 1 1 20%;
            }

            .topic-metadata {
                color: #102C3D;
                text-align: center;
                line-height: 150%;
            }

            .buttons-container {
                display: flex;
                align-items: center;
                justify-content: flex-end;
                flex-wrap: wrap;
                flex: 2 1 50%;
            }

            .buttonSection {
                margin: 0.1rem 0.25rem;
            }

            .current-privacy-status {
                cursor: default;
            }

            #privacyMenu {
                --mdc-menu-min-width: 280px;
                --mdc-menu-max-width: 320px;
            }

            .privacy-status-row {
                padding: 0.75rem 1rem 0.5rem;
                cursor: default;
            }

            .privacy-status-title {
                font-size: 0.9rem;
                color: var(--theme-input-color);
            }

            .privacy-option {
                padding: 0.6rem 1rem;
                cursor: pointer;
                display: block;
                outline: none;
            }

            .privacy-option:hover,
            .privacy-option:focus {
                background-color: var(--theme-hover-color, rgba(0, 0, 0, 0.04));
            }

            .privacy-option-title {
                display: flex;
                align-items: center;
                gap: 0.4rem;
                font-size: 0.9rem;
                color: var(--theme-input-color);
            }

            .privacy-description {
                font-size: 0.75rem;
                color: var(--theme-input-color);
                opacity: 0.7;
                white-space: normal;
                line-height: 1.15rem;
                margin-top: 0.15rem;
            }

            .privacy-option .privacy-description {
                margin-left: 1.4rem;
            }

            li[divider] {
                border-top: 1px solid var(--theme-border-color, rgba(0, 0, 0, 0.12));
                margin: 0.25rem 0;
                list-style: none;
            }

            .shareButtonIcon {
                --mdc-icon-size: 1rem;
            }

            .shareButtonImg {
                width: 1.0rem;
                height: 1.0rem;
            }

            .leftPng {
                height: 0.75rem;
            }

            .gatedField {
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 5px;
            }

            .hidden {
                display: none;
            }

            .add-insights-button {
                color: #EA8567;
                text-align: center;
                font-weight: 700;
                line-height: 130%;
                text-decoration: underline;
                cursor: pointer;
                display: flex;
                align-items: center;
                flex-direction: row;
            }

            .menuPng {
                height: 24px;
            }

            .buttonText {
                display: none;
                opacity: 0;
                transition: visibility 0s, opacity 0.5s linear;
                margin-left: 5px;
            }

            form-button:hover .buttonText {
                display: inline;
                visibility: visible;
                opacity: 1;
            }
        `;
    }
}